import { createSlice } from "@reduxjs/toolkit";

/**
 * What the agent surface will let this reader do, and what they have said about its answers.
 *
 * react-query stays the fetch cache; this slice holds only what a component outside the
 * fetching hook has to read synchronously. Two things qualify:
 *
 *  - **capabilities**, because the rail and the page both gate on them and neither owns the
 *    fetch. One cached query, read in two trees.
 *  - **voteCache**, because no chat payload reports a stored score and there is no endpoint
 *    that reads a session's scores back. Without the mirror a thumb would render unrated the
 *    moment the reader navigated away and returned.
 *  - **knownTags** and **feedbackNote**, because every thumb group on the page shares them: the
 *    tags this reader has used before are offered in whichever note opens next, and only one
 *    note is open at a time, whichever answer or citation it belongs to.
 */
export const agentSlice = createSlice({
    name: "agent",
    initialState: {
        /** GET /api/agent/ -- corpora, result modes and per-capability allowances. */
        capabilities: null,
        /** Thumb votes cast in this session, keyed by session + message. */
        voteCache: {},
        /**
         * Feedback tags this reader has used, newest first. Seeded by each feedback read's
         * `known_labels` and added to as tags are saved, so the second note of a visit offers
         * what the first one created without another round trip.
         */
        knownTags: [],
        /** The one open feedback note: `{key, value, mode: "click"|"hover", engaged}`, or null. */
        feedbackNote: null,
    },
    reducers: {
        setCapabilities: (state, { payload }) => {
            state.capabilities = payload;
        },
        setVote: (state, { payload: { key, value } }) => {
            state.voteCache[key] = value;
        },
        /** `payload` is newest first, and lands at the front in that order. */
        rememberTags: (state, { payload = [] }) => {
            const fresh = payload.filter(Boolean);
            state.knownTags = [...new Set([...fresh, ...state.knownTags])];
        },
        openFeedbackNote: (state, { payload: { key, value, mode } }) => {
            state.feedbackNote = { key, value, mode, engaged: false };
        },
        /** Patch the open note -- its mode, value or engaged flag -- if it is still the one open. */
        updateFeedbackNote: (state, { payload: { key, ...changes } }) => {
            if (state.feedbackNote?.key === key) Object.assign(state.feedbackNote, changes);
        },
        /** With a key, closes only that note: never one opened over something else meanwhile. */
        closeFeedbackNote: (state, { payload } = {}) => {
            if (!payload || state.feedbackNote?.key === payload) state.feedbackNote = null;
        },
        resetAgent: (state) => {
            state.capabilities = null;
            state.voteCache = {};
            state.knownTags = [];
            state.feedbackNote = null;
        },
    },
});

export const {
    setCapabilities,
    setVote,
    rememberTags,
    openFeedbackNote,
    updateFeedbackNote,
    closeFeedbackNote,
    resetAgent,
} = agentSlice.actions;

/** AI surfaces are unavailable when the capabilities call came back 403 or has not loaded. */
export const selectAgentAllowed = ({ agent }) => Boolean(agent.capabilities && !agent.capabilities.denied);

/** True once GET /api/agent/ has answered: a capability index, or `{denied}` on a 403. */
export const selectCapabilitiesLoaded = ({ agent }) => agent.capabilities !== null;

export const selectCapability =
    (name) =>
    ({ agent }) =>
        Boolean(agent.capabilities?.capabilities?.[name]?.allowed);

/**
 * Corpora the reader's active subscriptions grant, as GET /api/agent/ reports them.
 *
 * These are data-api's own ResourceApp codes (`optm`, `insights`, `pi`), not this app's index
 * slugs. The two vocabularies agree on some codes and not others, and keying a picker on the
 * wrong one is how a corpus a reader had bought stayed invisible to them.
 */
export const selectCorpora = ({ agent }) => agent.capabilities?.corpora || [];

/**
 * What to call each of those corpora, as the server's corpus registry names them.
 *
 * `{}` until the capabilities call lands, and `{}` on an arbo-api that does not send them --
 * callers fall back to their own labels for the codes they know. The point of having it at all
 * is that the registry is where corpora are added, so a client keeping its own list silently
 * omits any corpus added after it was written.
 */
export const selectCorpusLabels = ({ agent }) => agent.capabilities?.corpus_labels || {};

/**
 * The scopes this reader may ask under, in rail order.
 *
 * Each already carries its label and its one-line hint, because the server owns both: a scope is
 * added there, and a client keeping its own label table silently omits any scope added after it
 * was written -- or, worse, names one that does not exist.
 *
 * `[]` on an arbo-api whose catalog has not been seeded, which the rail reads as "no picker"
 * rather than as a failure.
 */
export const selectAgentScopes = ({ agent }) => agent.capabilities?.scopes || [];

/**
 * The reader's bookmarked pipelines, newest first, as `{id, name}`. They fill the pipeline in a
 * new chat's example questions. `[]` on an arbo-api that does not send them.
 */
export const selectBookmarkedPipelines = ({ agent }) => agent.capabilities?.bookmarked_pipelines || [];

export default agentSlice.reducer;
